import { Input } from "@/components/ui/input";
import { fetchBanks, filterBanks, type BankOption } from "@/lib/sales/banksStore";
import { cn } from "@/lib/utils";
import { Search } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

type Props = {
  value: string;
  onChange: (bankName: string) => void;
  disabled?: boolean;
  placeholder?: string;
  inputClassName?: string;
};

/**
 * Inline bank search/select. Renders the list inside the component (not a body portal)
 * so it works inside Radix Dialogs without outside-click / focus-trap fights.
 */
export function PakistanBankPicker({
  value,
  onChange,
  disabled,
  placeholder = "Search bank…",
  inputClassName,
}: Props) {
  const [banks, setBanks] = useState<BankOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState<number>(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void fetchBanks()
      .then((rows) => {
        if (!cancelled) setBanks(rows);
      })
      .catch(() => {
        if (!cancelled) setBanks([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => filterBanks(banks, search), [banks, search]);

  useEffect(() => {
    setHighlightIndex(filtered.length > 0 ? 0 : -1);
  }, [filtered, search, open]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node;
      if (containerRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  useEffect(() => {
    if (highlightIndex >= 0 && listRef.current) {
      const ul = listRef.current.querySelector("ul");
      const activeLi = ul?.children[highlightIndex] as HTMLElement;
      if (activeLi) {
        activeLi.scrollIntoView({ block: "nearest" });
      }
    }
  }, [highlightIndex]);

  const clearSelection = () => {
    onChange("");
    setSearch("");
    setOpen(true);
  };

  const selectedBank = useMemo(() => {
    if (!value) return null;
    const valStr = String(value).trim();
    return (
      banks.find(
        (b) =>
          (b.chartOfAccountsId != null && String(b.chartOfAccountsId) === valStr) ||
          String(b.id) === valStr ||
          b.name.toLowerCase() === valStr.toLowerCase(),
      ) ?? null
    );
  }, [banks, value]);

  const displayValue = selectedBank
    ? selectedBank.code
      ? `${selectedBank.name} (${selectedBank.code})`
      : selectedBank.name
    : value || search;

  const selectBank = (bank: BankOption) => {
    const val = bank.chartOfAccountsId != null ? String(bank.chartOfAccountsId) : String(bank.id);
    onChange(val);
    setSearch("");
    setOpen(false);
  };

  return (
    <div ref={containerRef} className={cn("relative z-40", open && "z-[100]")}>
      <Search className="pointer-events-none absolute left-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <Input
        className={cn(
          "glass-input h-10 w-full pl-9 text-sm",
          value && "cursor-pointer bg-muted/25 text-foreground",
          inputClassName,
        )}
        value={displayValue}
        readOnly={Boolean(value)}
        disabled={disabled}
        autoComplete="off"
        aria-autocomplete="list"
        aria-expanded={open && !value}
        placeholder={value ? "" : placeholder}
        onChange={(e) => {
          if (value) return;
          setSearch(e.target.value);
          setOpen(true);
        }}
        onClick={() => {
          if (value) {
            clearSelection();
            return;
          }
          setOpen(true);
        }}
        onFocus={() => {
          if (value) return;
          setOpen(true);
        }}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            setOpen(false);
            return;
          }
          if (value && (e.key === "Backspace" || e.key === "Delete")) {
            clearSelection();
            return;
          }
          if (!open && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
            setOpen(true);
            return;
          }
          if (open && filtered.length > 0) {
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setHighlightIndex((prev) => (prev < filtered.length - 1 ? prev + 1 : 0));
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              setHighlightIndex((prev) => (prev > 0 ? prev - 1 : filtered.length - 1));
            } else if (e.key === "Enter") {
              e.preventDefault();
              const selected = filtered[highlightIndex] ?? filtered[0];
              if (selected) {
                selectBank(selected);
              }
            }
          }
        }}
      />

      {open && !value ? (
        <div
          ref={listRef}
          role="listbox"
          className="absolute left-0 right-0 top-[calc(100%+4px)] z-[300] max-h-56 overflow-y-auto rounded-md border border-border bg-popover text-popover-foreground shadow-xl"
        >
          {loading ? (
            <div className="px-3 py-4 text-center text-sm text-muted-foreground">Loading banks…</div>
          ) : filtered.length === 0 ? (
            <div className="px-3 py-4 text-center text-sm text-muted-foreground">No matching banks.</div>
          ) : (
            <ul className="p-1">
              {filtered.map((bank, index) => (
                <li key={bank.id}>
                  <button
                    type="button"
                    role="option"
                    className={cn(
                      "w-full rounded-sm px-2 py-2 text-left text-sm outline-none transition-colors",
                      index === highlightIndex
                        ? "bg-accent text-accent-foreground font-medium"
                        : "hover:bg-accent hover:text-accent-foreground",
                    )}
                    onMouseEnter={() => setHighlightIndex(index)}
                    onMouseDown={(e) => {
                      // Prevent input blur from closing before the value is applied.
                      e.preventDefault();
                      selectBank(bank);
                    }}
                  >
                    <span className="flex items-center justify-between gap-2 w-full">
                      <span className="font-medium truncate">{bank.name}</span>
                      {bank.code ? (
                        <span className="text-xs opacity-75 font-normal shrink-0">({bank.code})</span>
                      ) : null}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}
